import constants from '@/Constants';
import { EventReport } from '@/modules/pdf/event-report';
import { EventReportResponse } from '@/modules/pdf/event-report/types';
import { GoiasaReport } from '@/modules/pdf/goiasa';
import { GoiasaAlertReport } from '@/modules/pdf/goiasa-alert';
import { GoiasaDangersAndRisksReport } from '@/modules/pdf/goiasa-dangers-and-risks';
import { EventReportData } from '@/modules/pdf/goiasa-dangers-and-risks/types';
import { GoiasaEfficiencyVerification } from '@/modules/pdf/goiasa-efficiency-verification';
import { GoiasaIncidentAnalysis } from '@/modules/pdf/goiasa-incident-analysis';
import { GoiasaAccidentAnalysis } from '@/modules/pdf/goiasa-accident-analysis';
import { GoiasaPermitAssessment } from '@/modules/pdf/goiasa-permit-assessment';
import { GoiasaRiskSeries } from '@/modules/pdf/goiasa-risk-series';
import { GoiasaSafetyObservation } from '@/modules/pdf/goiasa-safety-observation';
import { GoiasaUnsafeActReport } from '@/modules/pdf/goiasa-unsafe-act';
import { ChecklistVerificacaoFerramentasReport } from '@/modules/pdf/goiasa-verificacao-ferramentas';
import { GoiasaDeviationsReport } from '@/modules/pdf/goiasa-deviations';
import { GoiasaDeviationsNewReport } from '@/modules/pdf/goiasa-deviations-new';
import { IssmaPlantioMecanizadoReport } from '@/modules/pdf/goiasa-plantio-mecanizado';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

export const ReportGoiasa: React.FC<{
  reportId: string;
  workspaceSlug: string;
}> = ({ reportId, workspaceSlug }) => {
  const [report, setReport] = useState<EventReportResponse | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const url = constants.REPORTS_API_URL || '';

        const parsedUrl = url
          .toLowerCase()
          .replace('{{workspace}}', workspaceSlug);

        const { data, status } = await axios.get<EventReportResponse>(
          `${parsedUrl}pdf_json_by_export_id?export_id=${reportId}`,
          {
            headers: {
              Authorization: constants.REPORTS_API_AUTHORIZATION_TOKEN,
            },
          },
        );

        if (status === 200) {
          setReport(data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchReport();
  }, [reportId, workspaceSlug]);

  const getReport = () => {
    if (report?.header?.parentable_type === 'ticket') {
      return (
        <EventReport
          reportData={{
            body: {
              items: report?.body || [],
            },
            header: report?.header,
          }}
        />
      );
    }

    if (
      report?.body?.[0]?.form_name === 'Registro de Desvios - Novo' ||
      report?.header?.checklist_id?.includes('593a56ee-3741-48ec-99d8-ff712abdce9b')
    ) {
      return (
        <GoiasaDeviationsNewReport
          reportData={
            {
              body: report?.body,
              header: report?.header,
            } as any
          }
        />
      );
    }

    switch (report?.body?.[0]?.form_name) {
      case 'Comunicado Acidente/Incidente':
        return (
          <GoiasaAlertReport
            reportData={{
              body: report?.body,
              header: report?.header,
            }}
          />
        );

      case 'FORMU-0967 - Avaliação Permissão de Trabalho':
        return (
          <GoiasaPermitAssessment
            reportData={{
              body: report?.body,
              header: report?.header,
            }}
          />
        );

      case 'FORMU - 0831 - Levantamento de Perigos e Riscos':
        return (
          <>
            <GoiasaDangersAndRisksReport
              reportData={
                {
                  body: {
                    items: report?.body || [],
                  },
                  header: report?.header,
                } as EventReportData
              }
            />
          </>
        );

      case 'FORMU 1112 - Análise de Ocorrências - Rev. 18-03-2025':
        return (
          <>
            <GoiasaIncidentAnalysis
              reportData={
                {
                  body: {
                    items: report?.body || [],
                  },
                  header: report?.header,
                } as EventReportData
              }
            />
          </>
        );

      case 'FORMU-1112 Análise de Acidentes':
        return (
          <>
            <GoiasaAccidentAnalysis
              reportData={
                {
                  body: {
                    items: report?.body || [],
                  },
                  header: report?.header,
                } as EventReportData
              }
            />
          </>
        );

      case 'Série de Risco':
        return (
          <>
            <GoiasaRiskSeries
              reportData={
                {
                  body: {
                    items: report?.body || [],
                  },
                  header: report?.header,
                } as EventReportData
              }
            />
          </>
        );

      case 'FORMU 1112 - Verificação de Eficácia - Rev. 18-03-2025':
        return (
          <>
            <GoiasaEfficiencyVerification
              reportData={
                {
                  body: {
                    items: report?.body || [],
                  },
                  header: report?.header,
                } as EventReportData
              }
            />
          </>
        );

      case 'Observação de Segurança - TST':
        return (
          <GoiasaSafetyObservation
            reportData={{
              body: report?.body,
              header: report?.header,
            }}
          />
        );

      case 'FORMU-0650 -  Comunicado Ato Inseguro':
        return (
          <GoiasaUnsafeActReport
            reportData={
              {
                body: report?.body,
                header: report?.header,
              } as any
            }
          />
        );

      case 'Registro de Desvios':
        return (
          <GoiasaDeviationsReport
            reportData={
              {
                body: report?.body,
                header: report?.header,
              } as any
            }
          />
        );

      case 'Checklist - Verificação de Ferramentas':
        return (
          <ChecklistVerificacaoFerramentasReport
            reportData={
              {
                body: report?.body,
                header: report?.header,
              } as any
            }
          />
        );
      case 'ISSMA - Plantio Mecanizado':
        return (
          <IssmaPlantioMecanizadoReport
            reportData={
              {
                body: report?.body,
                header: report?.header,
              } as any
            }
          />
        );

      default:
        return (
          <>
            {report?.body.map((item) => (
              <GoiasaReport
                reportData={{
                  body: item,
                  header: report?.header,
                }}
              />
            ))}
          </>
        );
    }
  };

  if (!report?.body || !report?.header) return <div />;

  return <div style={{ height: '100%', width: '100%' }}>{getReport()}</div>;
};
